import { FilterOperator } from "src/core/model/FilterOperator";
import { VariableFilter } from "src/core/model/VariableFilter";

/**
 * Applies filter conditions to variable values.
 * Mirrors Android/iOS FilterEngine logic.
 *
 * Trigger filters use AND logic: ALL filters must pass.
 * For arrays/iterables, the filter is applied to each element.
 */
export class FilterEngine {
  /**
   * Returns true if ALL filters pass against the given variables.
   */
  static passesTriggerFilters(filters: VariableFilter[] | undefined, variables: Map<string, unknown>): boolean {
    if (!filters || filters.length === 0) return true;
    return filters.every((filter) => {
      const value = variables.get(filter.field);
      return FilterEngine.applyFilter(value, filter.operator, filter.value);
    });
  }

  /**
   * Apply a single filter operator to a field value.
   */
  static applyFilter(fieldValue: unknown, operator: FilterOperator, filterValue: string): boolean {
    if (fieldValue === null || fieldValue === undefined) {
      // Null/undefined values only pass NOT operators
      return FilterEngine.isNegationOperator(operator);
    }

    // Handle arrays: filter passes if ANY element matches
    if (Array.isArray(fieldValue)) {
      const anyMatch = fieldValue.some((item) => FilterEngine.applySingleFilter(String(item), operator, filterValue));
      return FilterEngine.isNegationOperator(operator) ? !anyMatch : anyMatch;
    }

    return FilterEngine.applySingleFilter(String(fieldValue), operator, filterValue);
  }

  private static applySingleFilter(value: string, operator: FilterOperator, filterValue: string): boolean {
    switch (operator) {
      case FilterOperator.IS_EQUALS:
        return value === filterValue;
      case FilterOperator.IS_EQUALS_IGNORE_CASE:
        return value.toLowerCase() === filterValue.toLowerCase();
      case FilterOperator.NOT_EQUALS:
        return value !== filterValue;
      case FilterOperator.NOT_EQUALS_IGNORE_CASE:
        return value.toLowerCase() !== filterValue.toLowerCase();

      case FilterOperator.IS_CONTAINS:
        return value.includes(filterValue);
      case FilterOperator.IS_CONTAINS_IGNORE_CASE:
        return value.toLowerCase().includes(filterValue.toLowerCase());
      case FilterOperator.NOT_CONTAINS:
        return !value.includes(filterValue);
      case FilterOperator.NOT_CONTAINS_IGNORE_CASE:
        return !value.toLowerCase().includes(filterValue.toLowerCase());

      case FilterOperator.IS_STARTS_WITH:
        return value.startsWith(filterValue);
      case FilterOperator.IS_STARTS_WITH_IGNORE_CASE:
        return value.toLowerCase().startsWith(filterValue.toLowerCase());
      case FilterOperator.NOT_STARTS_WITH:
        return !value.startsWith(filterValue);
      case FilterOperator.NOT_STARTS_WITH_IGNORE_CASE:
        return !value.toLowerCase().startsWith(filterValue.toLowerCase());

      case FilterOperator.IS_ENDS_WITH:
        return value.endsWith(filterValue);
      case FilterOperator.IS_ENDS_WITH_IGNORE_CASE:
        return value.toLowerCase().endsWith(filterValue.toLowerCase());
      case FilterOperator.NOT_ENDS_WITH:
        return !value.endsWith(filterValue);
      case FilterOperator.NOT_ENDS_WITH_IGNORE_CASE:
        return !value.toLowerCase().endsWith(filterValue.toLowerCase());

      case FilterOperator.IS_REGEX_MATCH:
        return new RegExp(filterValue).test(value);
      case FilterOperator.NOT_REGEX_MATCH:
        return !new RegExp(filterValue).test(value);

      case FilterOperator.LESS_THAN:
        return Number(value) < Number(filterValue);
      case FilterOperator.LESS_THAN_OR_EQUALS:
        return Number(value) <= Number(filterValue);
      case FilterOperator.GREATER_THAN:
        return Number(value) > Number(filterValue);
      case FilterOperator.GREATER_THAN_OR_EQUALS:
        return Number(value) >= Number(filterValue);

      default:
        return false;
    }
  }

  private static isNegationOperator(operator: FilterOperator): boolean {
    return (
      operator === FilterOperator.NOT_EQUALS ||
      operator === FilterOperator.NOT_EQUALS_IGNORE_CASE ||
      operator === FilterOperator.NOT_CONTAINS ||
      operator === FilterOperator.NOT_CONTAINS_IGNORE_CASE ||
      operator === FilterOperator.NOT_STARTS_WITH ||
      operator === FilterOperator.NOT_STARTS_WITH_IGNORE_CASE ||
      operator === FilterOperator.NOT_ENDS_WITH ||
      operator === FilterOperator.NOT_ENDS_WITH_IGNORE_CASE ||
      operator === FilterOperator.NOT_REGEX_MATCH
    );
  }
}
